import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import {
  bookingsCalendarClick,
  bookingsCalendarDatePickerLoad,
  bookingsCalendarErrorMessages,
  bookingsCalendarPageLoaded,
  bookingsCalendarTimePickerLoad,
  bookingsContactInfoSaveSuccess,
} from '@wix/bi-logger-wixboost-ugc/v2';
import {
  bookingsUoUFlowWidgetLoadSrc16Evid32,
  bookingsUoUFlowBookInitialIntentSrc16Evid33,
  bookingsUoUFlowSlotSelectionCompletedSrc16Evid34,
} from '@wix/bi-logger-bookings-data/v2';
import {
  BookingsQueryParams,
  getUrlQueryParamValue,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import { DailyAgendaState } from '../../components/DailyAgenda/state/types';
import {
  GetState,
  Subscribe,
} from '../../components/DailyAgenda/state/createState';
import {
  TriggeredByOptions,
  WidgetComponents,
  WidgetElements,
  REFERRAL_INFO,
  WIDGET_NAME,
} from './consts';
import { TimeSlot } from '@wix/ambassador-bookings-availability-v2-time-slot/types';
import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';

export class DailyAgendaBiLogger {
  private readonly viewerBiLogger;
  static readonly PAGE_NAME = 'daily_agenda_widget_v2';
  static readonly REFERRAL_INFO = REFERRAL_INFO;
  static readonly LAYOUT = 'daily_agenda_v2_weekly_picker';
  static readonly WIDGET_NAME = WIDGET_NAME;
  constructor(
    private readonly flowAPI: ControllerFlowAPI,
    private readonly getState: GetState,
  ) {
    this.viewerBiLogger = flowAPI.bi;
    this.initializeDefaults();
  }

  private mapStateToDefaultBiParams(state: DailyAgendaState) {
    return {
      selectedTimezone: state?.selectedTimezone,
      selectedDate: state?.selectedDate,
      selectedFilters: JSON.stringify(state?.selectedFilters),
      selectedRange: JSON.stringify(state?.selectedRange),
      businessProperties: JSON.stringify({
        language: state?.languageSettings.language,
        countryCode: state?.languageSettings.countryCode,
      }),
      // TODO: add layout
    };
  }

  private initializeDefaults() {
    this.viewerBiLogger?.updateDefaults({
      pageName: DailyAgendaBiLogger.PAGE_NAME,
      ...this.mapStateToDefaultBiParams(this.getState()),
      referralInfo: DailyAgendaBiLogger.REFERRAL_INFO,
    });
  }

  subscribeToStateChanges(subscribe: Subscribe) {
    subscribe(
      ({
        selectedDate,
        selectedTimezone,
        selectedRange,
        selectedFilters,
        languageSettings,
        settings,
      }) => ({
        selectedDate,
        selectedTimezone,
        selectedRange,
        selectedFilters,
        languageSettings,
        settings,
      }),
      () => {
        this.update();
      },
    );
  }

  update() {
    this.viewerBiLogger?.updateDefaults(
      this.mapStateToDefaultBiParams(this.getState()),
    );
  }

  reportClick(
    component: WidgetComponents,
    element: WidgetElements,
    properties?: Record<string, any>,
  ) {
    this.viewerBiLogger?.report(
      bookingsCalendarClick({
        component,
        element,
        properties: properties ? JSON.stringify(properties) : undefined,
        layout: DailyAgendaBiLogger.LAYOUT,
      }),
    );
  }

  reportDatePickerLoad(dateAvailability: any, triggeredBy: TriggeredByOptions) {
    this.viewerBiLogger?.report(
      bookingsCalendarDatePickerLoad({
        dateAvailability: JSON.stringify(dateAvailability),
        triggeredBy,
      }),
    );
  }

  reportSessionsLoaded(
    triggeredBy: TriggeredByOptions,
    selectedDate: Date,
    timeSlots: TimeSlot[],
  ) {
    this.viewerBiLogger?.report(
      bookingsCalendarTimePickerLoad({
        triggeredBy,
        selectedDate: selectedDate.toISOString(),
        timeSlotsAvailability: JSON.stringify(timeSlots),
      }),
    );
  }
  reportInitialIntentToBook(
    serviceType: ServiceType,
    bookingsServiceId: string,
    isEligibleForFlowCompletion: boolean,
  ) {
    this.viewerBiLogger?.report(
      bookingsUoUFlowBookInitialIntentSrc16Evid33({
        serviceType,
        bookingsServiceId,
        isEligibleForFlowCompletion,
        widget_name: DailyAgendaBiLogger.WIDGET_NAME,
      }),
    );
  }

  reportSlotSelectionCompleted(
    serviceType: ServiceType,
    bookingsServiceId: string,
    startTime: Date,
    endTime: Date,
    isEligibleForFlowCompletion: boolean,
  ) {
    this.viewerBiLogger?.report(
      bookingsUoUFlowSlotSelectionCompletedSrc16Evid34({
        serviceType,
        bookingsServiceId,
        startTime: startTime.getTime(),
        endTime: endTime.getTime(),
        isEligibleForFlowCompletion,
        widget_name: DailyAgendaBiLogger.WIDGET_NAME,
      }),
    );
  }

  reportContactInfoSaveSuccess() {
    this.viewerBiLogger?.report(bookingsContactInfoSaveSuccess({}));
  }

  reportError(error: string) {
    this.viewerBiLogger?.report(
      bookingsCalendarErrorMessages({ errorMessage: error }),
    );
  }

  reportPageLoaded() {
    this.viewerBiLogger?.report(bookingsCalendarPageLoaded({}));
  }

  reportWidgetLoad() {
    this.viewerBiLogger?.report(
      bookingsUoUFlowWidgetLoadSrc16Evid32({
        widget_name: DailyAgendaBiLogger.PAGE_NAME,
        referralInfo: getUrlQueryParamValue(
          this.flowAPI.controllerConfig.wixCodeApi,
          BookingsQueryParams.REFERRAL,
        ),
      }),
    );
  }
}
